import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthApi } from "../../api/common/apiIndex";
import CountrySelect from "../common/commonUtils/CountrySelect";
import { MdBadge } from "react-icons/md";

export default function BusinessDetailsForm({
  value,
  onChange,
  onNameAvailabilityChange,
  title = "Business details",
  step = 1,
}) {
  const v = value || {};
  const update = (patch) => onChange?.({ ...v, ...patch });
  const businessName = useMemo(() => (v.name || "").trim(), [v.name]);
  const [debouncedName, setDebouncedName] = useState(businessName);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(businessName), 400);
    return () => clearTimeout(timer);
  }, [businessName]);

  const nameAvailability = useQuery({
    queryKey: ["business-name-available", debouncedName.toLowerCase()],
    queryFn: () => AuthApi.checkBusinessNameAvailable(debouncedName),
    enabled: debouncedName.length >= 2,
    staleTime: 60_000,
    retry: false,
  });

  const isCheckingName =
    businessName.length >= 2 &&
    (businessName !== debouncedName || nameAvailability.isFetching);
  const nameAvailable =
    debouncedName.length >= 2 && nameAvailability.data?.available === true;
  const nameTaken =
    debouncedName.length >= 2 && nameAvailability.data?.available === false;

  useEffect(() => {
    onNameAvailabilityChange?.({
      checkedName: debouncedName,
      isChecking: isCheckingName,
      available: debouncedName.length >= 2 ? nameAvailability.data?.available ?? null : null,
      isTaken: nameTaken,
    });
  }, [
    debouncedName,
    isCheckingName,
    nameAvailability.data?.available,
    nameTaken,
    onNameAvailabilityChange,
  ]);

  return (
    <section className="ui-panel">
      <header className="ui-panel__header">
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="ui-title mb-0">{title}</h3>
          <span className="badge bg-body border text-body d-flex align-items-center gap-1">
            <MdBadge /> Step {step}
          </span>
        </div>
      </header>

      <div className="p-md-4">
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Business Name*</label>
            <input
              className={`form-control ${nameTaken ? "is-invalid" : ""}`}
              value={v.name || ""}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Baknay Express"
              required
            />
            {isCheckingName && (
              <div className="form-text">Checking business name....</div>
            )}
            {nameAvailable && !isCheckingName && (
              <div className="text-success small mt-1">Business name is available.</div>
            )}
            {nameTaken && !isCheckingName && (
              <div className="invalid-feedback d-block">
                This business name is already taken.
              </div>
            )}
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Country*</label>
            <CountrySelect
              value={v.country || ""}
              onChange={(name) => update({ country: name })}
              showFlagInValue
            />
          </div>
        </div>
      </div>
    </section>
  );
}
